import common from '../common';

const _updateItemHiglighting = Symbol('_updateItemHiglighting');
const _snap = Symbol('_snap');
const _onMouseDown = Symbol('_onMouseDown');
const _onMouseMove = Symbol('_onMouseMove');
const _onMouseUp = Symbol('_onMouseUp');

/**
 * From splash art
 * @class
 */
export default class UICarousel extends PIXI.Container {
    constructor() {
        super();
        // Constants.
        this.PAGE_WIDTH = 600;
        this.DEFAULT_ITEM_SCALE = 0; // To be later initialized.
        this.UNSELECTED_ITEM_SCALE_DELTA = 0.1; // If an item is not currently selected, scale it down by this amount.
        this.SELECTED_ITEM_EXIT_EASE_FACTOR = 0.2; // Decrease the distance of the swipe gesture for the selected item.

        // Attributes.
        this._pageContainer = null;
        this._interactiveArea = null;
        this._startX = 0;
        this._lastX = 0;
        this._currentX = 0;
        this._itemIndex = 0;

        // Signals.
        this.signals = {};
        this.signals.onIndexChanged = new P3.utils.MiniSignal();
        this.signals.onItemSelected = new P3.utils.MiniSignal();

        this._tweens = new P3.utils.TWEEN.Group();
    }

    init(items) {
        // TODO - consider swapping for a container
        // Interactive area.
        this._interactiveArea = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._interactiveArea.tint = 0xFFFFFF;
        this._interactiveArea.alpha = 0;
        this._interactiveArea.height = 400;
        this._interactiveArea.width = common.STAGE_WIDTH;
        this._interactiveArea.anchor = new PIXI.Point(0.5, 0.5);
        this._interactiveArea.buttonMode = true;
        this._interactiveArea.interactive = true;
        this._interactiveArea.mousedown = this._interactiveArea.touchstart = this[_onMouseDown].bind(this);
        this._interactiveArea.mouseup = this._interactiveArea.touchend = this[_onMouseUp].bind(this);
        this._interactiveArea.mouseupoutside = this._interactiveArea.touchendoutside = this[_onMouseUp].bind(this);

        this.addChild(this._interactiveArea);

        // Item container.
        this._pageContainer = new PIXI.Container();
        this._pageContainer.anchor = new PIXI.Point(0.5, 0.5);
        //	this._pageContainer.mask = this._mask; // see above masking issue
        this._pageContainer.x = -this.PAGE_WIDTH * 0.5;
        this.addChild(this._pageContainer);

        // Items.
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (this.DEFAULT_ITEM_SCALE === 0)
                this.DEFAULT_ITEM_SCALE = item.scale.x;

            // Page.
            const page = new PIXI.Container();
            //page.anchor = new PIXI.Point(0, 0.5); //anchor on a container?
            this._pageContainer.addChild(page);

            // So we can keep some distance between items.
            const pageBg = new PIXI.Sprite(PIXI.Texture.WHITE);
            pageBg.alpha = 0;
            pageBg.anchor.set(0, 0.5);
            pageBg.width = this.PAGE_WIDTH;
            pageBg.height = item.height;
            page.addChild(pageBg);
            // Item.
            item.x = item.width * 0.5 + (pageBg.width - item.width) * 0.5;
            page.addChild(item);
            // Initial scale.
            if (i > 0) {
                item.scale = new PIXI.Point(
                    this.DEFAULT_ITEM_SCALE - this.UNSELECTED_ITEM_SCALE_DELTA,
                    this.DEFAULT_ITEM_SCALE - this.UNSELECTED_ITEM_SCALE_DELTA
                );
            }

            page.x = i * page.width;
        }

        this._pageContainer.children[0].y -= 100;
        this._pageContainer.children[0].alpha = 0;

    }

    animateIn() {
        new P3.utils.TWEEN.Tween(this._pageContainer.children[0], this.tweens)
            .to({
                alpha: 1,
                y: this._pageContainer.children[0].y + 100
            }, 250)
            .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
            .start();
    }

    destroy(options) {
        this._tweens.removeAll();
        P3.utils.destroyAllSignals(this.signals);
        super.destroy();
    }

    update() {
        this.tweens.update();
        this[_updateItemHiglighting]();
    }

    next(callback) {
        if (this._itemIndex < this._pageContainer.children.length - 1) {
            this._itemIndex += 1;

            new P3.utils.TWEEN.Tween(this._pageContainer, this.tweens)
                .to({
                    x: -this._itemIndex * this.PAGE_WIDTH - this.PAGE_WIDTH * 0.5
                }, 250)
                .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                .onComplete(() => {
                    this.signals.onIndexChanged.dispatch();
                    callback && callback.call();
                })
                .start();

            P3.audio.playSound('sfx_ui_carousel_swipe_00');
        }
    }

    prev() {
        if (this._itemIndex > 0) {
            this._itemIndex -= 1;

            new P3.utils.TWEEN.Tween(this._pageContainer, this.tweens)
                .to({
                    x: -this._itemIndex * this.PAGE_WIDTH - this.PAGE_WIDTH * 0.5
                }, 250)
                .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                .onComplete(() => {
                    this.signals.onIndexChanged.dispatch();
                })
                .start();

            // Sfx.
            P3.audio.playSound('sfx_ui_carousel_swipe_00');
        }
    }

    _getPageContainerX() {
        return -this._itemIndex * this.PAGE_WIDTH - this.PAGE_WIDTH * 0.5;
    }

    [_updateItemHiglighting]() {
        if (!this._pageContainer)
            return;

        // Highlight selection.
        const pageFactor = Math.abs(this._pageContainer.x) / this.PAGE_WIDTH;
        let itemIndexAux = Math.floor(pageFactor);
        // Ease changing the selection of the current item.
        if (this._itemIndex === itemIndexAux) {
            if (Math.abs(this._itemIndex - pageFactor) <= 0.5 - this.SELECTED_ITEM_EXIT_EASE_FACTOR
                && this._itemIndex > 0)
                itemIndexAux -= 1;
            else if (Math.abs(this._itemIndex - pageFactor) >= 0.5 + this.SELECTED_ITEM_EXIT_EASE_FACTOR
                && this._itemIndex < this._pageContainer.children.length - 1)
                itemIndexAux += 1;
        }

        var itemIndex = 0;
        for (const page of this._pageContainer.children) {
            const item = page.children[1];
            if (!item._isTweening) {
                if (itemIndex == itemIndexAux && item.scale.x != this.DEFAULT_ITEM_SCALE) {
                    new P3.utils.TWEEN.Tween(item.scale, this.tweens)
                        .to({
                            x: this.DEFAULT_ITEM_SCALE,
                            y: this.DEFAULT_ITEM_SCALE
                        }, 120)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .onStart(() => {
                            item._isTweening = true;
                        })
                        .onComplete(() => {
                            item._isTweening = false;
                        })
                        .start();
                } else if (itemIndex != itemIndexAux && item.scale.x != this.DEFAULT_ITEM_SCALE - this.UNSELECTED_ITEM_SCALE_DELTA) {
                    new P3.utils.TWEEN.Tween(item.scale, this.tweens)
                        .to({
                            x: this.DEFAULT_ITEM_SCALE - this.UNSELECTED_ITEM_SCALE_DELTA,
                            y: this.DEFAULT_ITEM_SCALE - this.UNSELECTED_ITEM_SCALE_DELTA
                        }, 120)
                        .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                        .onStart(() => {
                            item._isTweening = true;
                        })
                        .onComplete(() => {
                            item._isTweening = false;
                        })
                        .start();
                }
            }

            ++itemIndex;
        }
    }

    [_snap]() {
        const pageFactor = Math.abs(this._pageContainer.x) / this.PAGE_WIDTH;
        let newItemIndex = Math.floor(pageFactor);
        // Ease changing the selection of the current item.
        if (newItemIndex === this._itemIndex) {
            if (Math.abs(newItemIndex - pageFactor) <= 0.5 - this.SELECTED_ITEM_EXIT_EASE_FACTOR
                && newItemIndex > 0)
                newItemIndex -= 1;
            else if (Math.abs(newItemIndex - pageFactor) >= 0.5 + this.SELECTED_ITEM_EXIT_EASE_FACTOR
                && newItemIndex < this._pageContainer.children.length - 1)
                newItemIndex += 1;
        }

        new P3.utils.TWEEN.Tween(this._pageContainer, this.tweens)
            .to({
                x: -newItemIndex * this.PAGE_WIDTH - this.PAGE_WIDTH * 0.5
            }, 250)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .onComplete(() => {
                this._itemIndex = newItemIndex;
                this.signals.onIndexChanged.dispatch();
            })
            .start();
    }

    [_onMouseDown](mouseData) {
        //TweenMax.killTweensOf(this._pageContainer);

        const localPos = mouseData.data.getLocalPosition(this._interactiveArea);
        this._startX = localPos.x;
        this._lastX = this._startX;
        this._currentX = this._startX;

        this._interactiveArea.mousemove = this._interactiveArea.touchmove = this[_onMouseMove].bind(this);
    }

    [_onMouseMove](mouseData) {
        const localPos = mouseData.data.getLocalPosition(this._interactiveArea);

        this._currentX = localPos.x;

        this._pageContainer.x += (this._currentX - this._lastX) * 100;
        if (this._pageContainer.x > -this.PAGE_WIDTH * 0.5)
            this._pageContainer.x = -this.PAGE_WIDTH * 0.5;
        else if (this._pageContainer.x < -(this._pageContainer.children.length - 0.5) * this.PAGE_WIDTH)
            this._pageContainer.x = -(this._pageContainer.children.length - 0.5) * this.PAGE_WIDTH;

        this._lastX = this._currentX;
    }

    [_onMouseUp](mouseData) {
        let result = false;
        if (Math.abs(this._startX - this._currentX) <= 0.01) {
            const selectedItem = this._pageContainer.children[this._itemIndex].children[1];
            const localPos = mouseData.data.getLocalPosition(selectedItem);
            if (Math.abs(localPos.x) <= selectedItem.width * 0.8
                && Math.abs(localPos.y) <= selectedItem.height * 0.8) {
                this.signals.onItemSelected.dispatch();

                result = true;
            }
        }

        if (!result) {
            this._interactiveArea.mousemove = this._interactiveArea.touchmove = null;

            this[_snap]();

            // Sfx.
            P3.audio.playSound('sfx_ui_carousel_swipe_00');
        }
    }

    get selectedIndex() {
        return this._itemIndex;
    }

    get tweens() {
        return this._tweens;
    }
}